import React, { lazy } from "react"
import PropTypes from "prop-types"
import GTM from "@src/services/gtm"
import { PageSeoPropTypes } from "@src/components/page-helmet"
const ResponsiveSection = lazy(
  () => import("@src/components/container-responsive-section"),
)
import { ImagePropTypes } from "@src/components/core-image"
import { ProfileTilePropTypes } from "@src/components/content-profile-tile"
import ContentHeroBox from "@src/components/reusable/content-hero-box"
import theme from "@src/styles/theme"
import styled from "styled-components"
import { ParagraphHeroBox } from "@src/components/reusable/content-hero-box"
import DifficultyCard from "@src/components/reusable/cards/difficulty-card"
const Link = lazy(() => import("@src/components/core-link"))
import BreadCrumbs from "@src/components/breadcrumbs"

const MPStyle = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 76px;
  padding-bottom: 76px;
`

const CardHolder = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 32px;
`

const PageStaticTourDifficulty = () => {
  // const {
  //   tenAdventures: {
  //     page: {
  //       uri: pageUri,
  //       images: { header: headerImage },
  //       seo: pageSeo,
  //     },
  //   },
  // } = data
  GTM.dataLayerPushPageInfo({
    template: "static-about-us",
  })

  return (
    <>
      {/* <PageHelmet
        title={normalizeTitle(pageSeo.title)}
        meta={{
          canonical: `${SITE_DOMAIN}${pageUri}`,
          description: pageSeo.metaDesc,
          robots: [pageSeo.metaRobotsNofollow, "index"],
          image: pageSeo.seoImage,
          openGraph: {
            type: pageSeo.opengraphType,
            modifiedTime: pageSeo.opengraphModifiedTime,
          },
          twitter: {
            data: [],
          },
        }}
        structuredData={generateOrganization({
          image: headerImage.src,
        })}
      /> */}
      <ResponsiveSection>
        <BreadCrumbs />
      </ResponsiveSection>
      <MPStyle>
        <ResponsiveSection>
          <ContentHeroBox
            title={"Tour difficulty assessment"}
            title2={"Find your suitable tour difficulty"}
            content={[
              `Are you ready to start planning your next adventure, but have concerns about how challenging a trip might be? We understand that every traveller is unique, which is why we've designed an easy to understand tour difficulty rating system. We've applied this system to every single one of our tours, so that you can find the perfect adventure that matches both your ability and expectations.`,
              `Our carefully crafted ratings take into account various factors like duration, activity level, and altitude, so whether you're a family seeking a casual escape to the Italian coast or an intrepid solo hiker yearning for a high-altitude challenge in Chilean Patagonia, you can easily determine whether or not an adventure is the right fit.`,
              `Remember, there's more to the perfect travel experience than picking a destination you are interested in; it's also about finding an adventure that aligns with your personal fitness, experience, and comfort level. With 10Adventures as your guide, the world of exploration is open to you—it's time to create unforgettable memories on your own terms!`,
            ]}
            background={theme.green}
            color={"dark"}
            image={
              "https://d3owbckoeihl9o.cloudfront.net/images/static-images/tour-difficulty/tour-difficulty.png"
            }
          />
        </ResponsiveSection>

        <ResponsiveSection>
          <CardHolder>
            {/* <DifficultyCard
                image="https://d3owbckoeihl9o.cloudfront.net/images/static-images/tour-difficulty/no-rating.png"
                description="No rating scale is perfect, and one of the best ways to assess whether or not a tour is the right fit for you is to look at the most difficult day on the itinerary. Ideally, you've managed a similar challenge before, and the level of activity should line up with your level of personal fitness. Remember, it's all about knowing your limits and setting yourself up for an amazing adventure!"
                descriptionBold="No rating"
                cta="View all no rating tours"
                level="No rating"
                link="/tours/"
              /> */}
            <DifficultyCard
              image="https://d3owbckoeihl9o.cloudfront.net/images/static-images/tour-difficulty/casual.png"
              level="Casual"
              descriptionBold="Level 1"
              description=" tours are casual experiences that focus on enjoying the moment. They are perfect for families, those who prefer a more relaxed pace, or anyone with mobility considerations. Imagine leisurely walking tours, sightseeing excursions, and stopping at lookout points to capture that perfect photo. These casual tours are ideal for entry level adventurers, as they can often be customized and made easier for guests – we're here to support and guide you every step of the way!"
              cta="View all casual tours"
              link="/tours/?difficulty=Very easy"
            />
            <DifficultyCard
              image="https://d3owbckoeihl9o.cloudfront.net/images/static-images/tour-difficulty/easy.png"
              level="Easy"
              descriptionBold="Level 2"
              description=" tours include activities of easy to moderate difficulty throughout. On these tours the activity typically takes up 3-5 hours each day, and are often over easier terrain, making them perfect for those looking to explore at a comfortable pace. These could be considered family friendly for older children who are keen adventurers."
              cta="View all easy tours"
              link="/tours/?difficulty=Easy"
            />
            <DifficultyCard
              image="https://d3owbckoeihl9o.cloudfront.net/images/static-images/tour-difficulty/moderate.png"
              level="Moderate"
              descriptionBold="Level 3"
              description=" tours are considered moderate trips and offer a full range of activities for guests who are intermediate adventurers. If you have some experience in the featured activity and are familiar with the gear that will be used on tour, you'll be right at home. For example, a level 3 backpacking trip will cover longer distances, so a moderate fitness level and some backcountry experience are great to have. While these trips may not suit young children or those with lower levels of personal fitness, they're perfect for travellers looking to explore the next level of adventure."
              cta="View all moderate tours"
              link="/tours/?difficulty=Moderate"
            />
            <DifficultyCard
              image="https://d3owbckoeihl9o.cloudfront.net/images/static-images/tour-difficulty/difficult.png"
              level="Difficult"
              descriptionBold="Level 4"
              description=" tours offer a difficult adventure, perfect for those with ample experience and who regularly take part in the chosen activity. If you are considering a Level 4 tour, you should have a good level of physical fitness and can expect longer and more intense activities, typically lasting between 5-8 hours each day. In some cases, difficult tours involve higher altitudes, adding an extra layer of adversity."
              cta="View all difficult tours"
              link="/tours/?difficulty=Hard"
            />
            <DifficultyCard
              image="https://d3owbckoeihl9o.cloudfront.net/images/static-images/tour-difficulty/challenging.png"
              level="Challenging"
              descriptionBold="Level 5"
              description=" tours are challenging to undertake, and should be reserved for travellers with plenty of experience, a strong level of physical fitness, and familiarity with the activity at hand. This is the top of our rating scale, and Level 5 itineraries often include high altitude, and long daily distances – think trekking in the Himalayas or a bike touring adventure with multiple ~100km days. You will need to be comfortable with pushing yourself through challenging circumstances, but rest assured that we are here to help get you started on the right path."
              cta="View all challenging tours"
              link="/tours/?difficulty=Very hard"
            />
          </CardHolder>
        </ResponsiveSection>

        <ResponsiveSection>
          <ParagraphHeroBox>
            Our team of adventure travel specialists is here to support you
            every step of the way, ensuring you have a safe and unforgettable
            experience. If you have any questions on the Tour Difficulty Rating
            system or need advice on selecting the appropriate tour,{" "}
            <Link to="/contact/">we&apos;re just a call away!</Link>
          </ParagraphHeroBox>
        </ResponsiveSection>
      </MPStyle>
    </>
  )
}
PageStaticTourDifficulty.propTypes = {
  data: PropTypes.shape({
    tenAdventures: PropTypes.shape({
      page: PropTypes.shape({
        title: PropTypes.string,
        uri: PropTypes.string,

        images: PropTypes.shape({
          header: PropTypes.shape({ ...ImagePropTypes }),
        }),
        seo: PropTypes.shape({ ...PageSeoPropTypes }),
      }),
      teamPage: PropTypes.shape({
        content: PropTypes.string,
      }),
      teamMembers: PropTypes.shape({
        nodes: PropTypes.arrayOf(
          PropTypes.shape({
            sort: PropTypes.number,
            ...ProfileTilePropTypes,
          }),
        ),
      }),
    }),
  }),
  pageContext: PropTypes.shape({
    teamPageId: PropTypes.number,
  }),
}
export default PageStaticTourDifficulty
// export const query = graphql`
//   query ($id: ID!, $teamPageId: ID!) {
//     tenAdventures {
//       page(id: $id, idType: DATABASE_ID) {
//         title
//         uri
//         images {
//           header {
//             ...TenAdventuresGQL_ImageSizesQuery
//           }
//         }
//         seo {
//           ...TenAdventuresGQL_PostSEO
//         }
//       }
//       teamPage: page(id: $teamPageId, idType: DATABASE_ID) {
//         content
//       }
//       teamMembers(first: 100) {
//         nodes {
//           sort: teamSort
//           name: title
//           role: teamRole
//           location: profileLocation
//           bio: content
//           images: profileImages {
//             profile {
//               ...TenAdventuresGQL_ImageSizesQuery
//             }
//             profileCropped {
//               ...TenAdventuresGQL_ImageSizesQuery
//             }
//           }
//           links: profileLinks {
//             url
//             icon
//           }
//         }
//       }
//     }
//   }
// `
